import GroupAddress from "../models/GroupAdress";
import { hideToast, showToast, updateToastProgress } from "../components/Toasts";
import { EventKey } from "../eventLogger/EventKeys";

import { createGlobalAddresses } from "./createGlobalAddresses";

import { createAddresses_B } from "./createAddresses_B";

import Logger from "../Logger";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import BaseDialog from "../modelDialogs/BaseDialog";
import type Device from "../models/Device";
import { itx } from "../translationItx/itx";

import { isUiModeDeveloper, isUiModeExpert } from "../uiMode";
import type Channel from "../models/Channel";
import { createAddresses_C } from "./createAddresses_C";
import { createAddresses_A } from "./createAddresses_A";
import { initSignalBundleDefinitions } from "../models/SignalBundleDefinitions";
import ReferenceArray from "../models/ReferenceArray";
import ButtonChannel from "../models/ButtonChannel";
import Multiplexer from "../models/Multiplexer";
import linkSceneSignals from "./linkSceneSignals";
import { linkEnvironmentsSignals } from "./linkEnvironmentSignals";
import { linkActors } from "./linkActors";
import { linkButtons } from "./linkButtons";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import type Consumer from "../models/Consumer";
import { useState } from "react";
import GroupAdressResultDialog from "../modelDialogs/GroupAdressResultDialog";
import { getSignalTypeBundle } from "../models/SignalBundles";
import SettingsDialog from "../modelDialogs/SettingsDialog";
import SignalType from "../models/SignalType";
import SignalTypes from "../models/SignalTypes";
import Signal from "../models/Signal";
import Room from "../models/Room";

const logger = new Logger();

let restrictToDivicesGlobal: Array<Device> | undefined;

const groupAddressGenerationInfos = {
  deletedGroupAddressReferenceCount: 0,
  newGroupAddressReferenceCount: 0,
  deletedGroupAddressCount: 0,
  createdGroupAddressCount: 0,
};

//
// clear referenced flags for all groupAddresses on all Levels
//

function clearSignalBackpointersForAllGroupAddresses() {
  state.addresses.forEach((a1) => {
    a1.children.forEach((a2) => {
      a2.children.forEach((a3) => {
        a3.clearSignalBackpointers();
      });
    });
  });
}

function setIsFixedAndIsNewForAllGroupAddresses() {
  state.addresses.forEach((a1) => {
    a1.children.forEach((a2) => {
      a2.children.forEach((a3) => {
        a3.isFixed = true;
        a3.isNew = false;
      });
    });
  });
}

function deleteUnreferencedGroupAddresses() {
  state.addresses.asArray().forEach((a1) => {
    a1.children.asArray().forEach((a2) => {
      //
      // "31/2, 31/3 - Manuelle Gruppenadressen" wid nicht gelöscht
      // da diese Gruppenadressen darin manuell angelegt wurden
      // und somit erhalten bleiben müssen
      //
      // Auch leer wird 31/2 nicht gelöscht,
      // damit man dort jederzeit  manuell mit "+" etc. Gruppenadressen anlegen kann
      //

      if (!a2.isManualGroupAddressFolder) {
        a2.children.forEach((a3) => {
          //
          // Alle GruppenAddressen die nicht von einem Geräte-Kanal benutzt werden
          // werden gelöscht
          //
          if (a3.getSignalBackpointers(true).length == 0) {
            if (a3.isManualGroupAddress) {
              // manual group addressesshould not be deleted here.
              //
              // but if we come here, we have an inconstiency.
              // manual group addresses should normaly always have a signal backpointer,
              //
              console.log("unreferenced manual groupAddress found: " + a3.fullAddress + " " + a3.name);
              return;
            } else {
              if (state.settings.deleteNotUsedSignalTypes) {
                const noUpdateNoModifiedStatus = a3.isNew;
                a3.delete(noUpdateNoModifiedStatus);
              }
            }
          }
        });

        //
        // Alle Mittelgruppen die leer sind werden gelöscht
        // except of 31/5 which is blocked range for manual ets addresses
        //
        if (a2.children.size == 0 && a2.fullAddress != "31/5") {
          a2.delete();
        }
      }
    });

    //
    // Alle Hauptgruppen die leer sind werden gelöscht
    //
    if (a1.children.size == 0) {
      a1.delete();
    }
  });
}

function backupChildenOfGroupAddresses() {
  state.addresses.initLastItems();

  state.addresses.forEach((a1) => {
    a1.isNew = false;
    a1.children.initLastItems();
    a1.children.forEach((a2) => {
      a2.isNew = false;
      a2.children.initLastItems();
      a2.children.forEach((a3) => {
        a3.isNew = false;
      });
    });
  });
}

function clearAndBackupUsagesForMultiplexer() {
  state.sceneGroup.sortBy("tagOrName").forEach((multiplexer) => {
    if (multiplexer.className == "Multiplexer") {
      (multiplexer as Multiplexer).signals.forEach((signal) => {
        const lastGroupAddresses = (signal.lastGroupAddresses = new ReferenceArray<GroupAddress>());

        signal.groupAddresses.forEach((ga) => {
          lastGroupAddresses.add(ga);
        });

        if (!signal.type.isStatus) {
          //
          // Actions Signals and their GroupAddresses
          // are Link Consumer Signals & GroupAddresses
          // we keep them unmodified
          //
          signal.groupAddresses.clear();
        }
      });
    }
  });
}

//
// delete all signals and group addresses of a consumer that are not in the CURRENT signal type bundle
//  this may occur when a consumer is changed from one channel type to another
//  and the new channel type has a different or less signals than the old channel type
//
function removeNoLongerUsedSignalsUndGroupAddressesFromConsumers() {
  state.allConsumers().forEach((consumer) => {
    const bundle = getSignalTypeBundle(consumer.channelType);

    for (const signal of consumer.signals.asArray()) {
      if (bundle.indexOf(signal.type) == -1) {
        for (const ga of signal.groupAddresses.asArray()) {
          ga.delete();
        }
        signal.delete();
      }
    }
  });
}

function clearAndBackupUsagesForDevices() {
  const devices = state.allDevices();
  devices.forEach((device) => {
    device.channels.forEach((channel) => {
      let isGroupButtonChannel = false;
      if (channel.className == "ButtonChannel") {
        if ((channel as ButtonChannel).isGroupButtonChannel) {
          isGroupButtonChannel = true;
        }
      }

      channel.signals.forEach((signal) => {
        const lastGroupAddresses = (signal.lastGroupAddresses = new ReferenceArray<GroupAddress>());

        // "...", creates a copy, to allow ".remove" inside the loop
        [...signal.groupAddresses.items].forEach((ga) => {
          lastGroupAddresses.add(ga);

          //
          // never remove manual group addresses
          //
          if (!ga.isManualGroupAddress) {
            if (!isGroupButtonChannel || signal.type.isStatus) {
              //
              // Actions Signals and their GroupAddresses
              // are Link Consumer Signals & GroupAddresses
              // we keep them unmodified
              //
              signal.groupAddresses.remove(ga);
            }
          }
        });
      });
    });
  });
}

//
// Suche alle Gruppenadressen deren Addresse zu groß ist,
// und setze deren ErrorFlag
// für alle anderen wird der Errorflag resetet
//
function findErrors() {
  const groupAddressToBigError = { groupAddressToBig: "" };
  state.addresses.forEach((a1) => {
    a1.flagErrors(groupAddressToBigError, parseInt(a1.address) > 31);

    a1.children.forEach((a2) => {
      a2.flagErrors(groupAddressToBigError, parseInt(a2.address) > 7);

      a2.children.forEach((a3) => {
        //
        // count number of ActorChannels
        //
        let numberOfActorChannels = 0;
        a3.getSignalBackpointers(true).forEach((signal) => {
          if ((signal.parent as Channel)?.isActorChannel) {
            numberOfActorChannels++;
          }
        });

        a3.flagErrors(groupAddressToBigError, parseInt(a3.address) > 255);

        a3.flagErrors(
          { groupAddressWithoutActor: "" },
          a1.address != "31" && a1.address != "1" && numberOfActorChannels == 0,
        );
      });
    });
  });

  for (const consumer of state.allConsumers()) {
    consumer.consistencyCheck(consumer.parent as Room);
    consumer.raiseOnChange();
  }
}

//
// Suche alle Gruppenadressen deren Addresse zu groß ist,
// und setze deren ErrorFlag
// für alle anderen wird der Errorflag resetet
//
function updateChangesForGroupAddresses() {
  groupAddressGenerationInfos.createdGroupAddressCount = 0;
  groupAddressGenerationInfos.deletedGroupAddressCount = 0;

  //
  // clear top level dirty childFlag
  //
  state.addresses.clearDirty();
  state.addresses.clearModifiedStatus();
  state.addresses.clearDirtyChilds();

  //
  // remove new Items from the deleted collection
  // these items a generated during addressGeneration but are not referenced
  //
  state.addresses.removeNewItemsFromDeletedItems();
  state.addresses.forEach((a1) => {
    a1.children.removeNewItemsFromDeletedItems();
    a1.children.forEach((a2) => {
      //
      // Remove all new items from the deletedItems list
      // as they are only temporary created
      //
      a2.children.removeNewItemsFromDeletedItems();
    });
  });

  //
  // clear all groupAddress dirty childFlags
  //
  state.addresses.forEach((a1) => {
    a1.clearDirtyChilds();
    a1.children.forEach((a2) => {
      a2.clearDirtyChilds();
      a2.children.forEach((a3) => {
        a3.clearDirtyChilds();
      });
    });
  });

  //
  // calulate dirtyChilds for all GroupAddresses
  //

  state.addresses.forEach((a1) => {
    a1.children.deletedItems?.forEach((a2Deleted) => {
      if (!a2Deleted.isNew) {
        a1.dirtyChilds = true;
        // console.log("deleted level 2 groupAddress found: " + a2Deleted.fullAddress)
      }
    });

    a1.children.forEach((a2) => {
      if (a2.isNew || a2.isDirty) {
        a1.dirtyChilds = true;
      }

      a2.children.lastItems?.forEach((a3Last) => {
        if (!a2.children.items.has(a3Last.id)) {
          if (a3Last.isManualGroupAddress) {
            //
            // manual group addressesshould not be deleted here.
            // they are moved to an now owner collection 31/2
            //
            return;
          } else {
            //
            // a3Last is deleted
            //
            //////////////////////// mwe test a2.children.addToDeletedItems(a3Last);
            a2.dirtyChilds = true;
          }
        }
      });

      a2.children.deletedItems?.forEach((a3Deleted) => {
        a2.dirtyChilds = true;
        groupAddressGenerationInfos.deletedGroupAddressCount++;
        // console.log("delete groupAddress found: " + a3Deleted.fullAddress)
      });

      a2.children.forEach((a3) => {
        if (a3.isNew) {
          groupAddressGenerationInfos.createdGroupAddressCount++;
          // console.log(`created new groupAddress found: #${groupAddressGenerationInfos.createdGroupAddressCount}: ${a3.fullAddress}`)
        } else {
          console.log(`groupAddress resused: ${a3.fullAddress} ${a3.name}`);
        }

        if (a3.isNew || a3.isDirty) {
          a2.dirtyChilds = true;
        }
        a3.raiseOnChangeUI();
      });
      a2.raiseOnChangeUI();
    });
    a1.raiseOnChangeUI();
  });
  state.addresses.raiseOnChangeUI();
}

function updateChangesForDevices() {
  function findGaByFullAddress(
    goupAdresses: Array<GroupAddress> | undefined,
    fullAddress: string,
  ): GroupAddress | undefined {
    if (!goupAdresses) {
      return undefined;
    }
    const res = goupAdresses.find((ga) => ga.fullAddress == fullAddress);
    return res;
  }

  const devices = state.allDevices();
  devices.forEach((device) => {
    device.channels.forEach((channel) => {
      channel.signals.forEach((signal) => {
        signal.groupAddresses.forEach((ga) => {
          if (findGaByFullAddress(signal.lastGroupAddresses?.items, ga.fullAddress)) {
            // usage has already been there
          } else {
            // console.log("new usage of groupAddress: " + ga.fullAddress + " " + signal.fullTag); // no itx

            groupAddressGenerationInfos.newGroupAddressReferenceCount++;
            signal.raiseOnChange("groupAddresses");
          }
        });

        signal.lastGroupAddresses?.forEach((ga) => {
          if (findGaByFullAddress(signal.groupAddresses?.items, ga.fullAddress)) {
            // usage is still there
          } else {
            signal.raiseOnChange("groupAddresses");
            groupAddressGenerationInfos.deletedGroupAddressReferenceCount++;
          }
        });
      });
    });
  });
}

//
// returns number of unreferenced GroupAddresses
//

function countUnreferenced(): number {
  let res = 0;

  state.addresses.forEach((a1) => {
    a1.children.forEach((a2) => {
      a2.children.forEach((a3) => {
        if (!a3.getIsReferenced(true)) {
          res++;
          console.log(`unreferenced: ${a3.fullAddress} ${a3.name}`);
        }
      });
    });
  });

  return res;
}

function crunchLevel3GroupAddresses() {
  state.addresses.sortIntBy("address").forEach((a1) => {
    a1.children.sortIntBy("address").forEach((a2) => {
      let level3Addresses = a2.children.sortIntBy("address");

      //
      // Dont crunch the following (fixed) GroupAddressGroups
      //
      let nextAddress = 0;

      if (a1.address == "0" && a2.address == "0") {
        //
        // 0/0/0 ist not allowed in ETS
        // so start with 0/0/1
        //

        nextAddress = 1;
      }

      if (!a2.isManualGroupAddressFolder && !a2.isEnvironmentFolder) {
        //
        // optimize addresses
        // keep fixed addresses
        //
        level3Addresses.forEach((a3) => {
          if (!a3.isFixed) {
            let addressInUse = false;
            do {
              addressInUse = level3Addresses.find((a) => a != a3 && parseInt(a.address) == nextAddress) != undefined;
              if (addressInUse) {
                nextAddress++;
              }
            } while (addressInUse);

            const newAddress = `${nextAddress}`;
            if (a3.address != newAddress) {
              a3.address = newAddress;
              a3.updateFullAddress();
              a3.raiseOnChange("address");
            }
          }

          nextAddress = parseInt(a3.address) + 1;
        });
      }
    });
  });
}

function fixDuplicateLevel3GroupAddresses() {
  state.addresses.sortIntBy("address").forEach((a1) => {
    a1.children.sortIntBy("address").forEach((a2) => {
      let level3Addresses = a2.children.sortIntBy("address");

      //
      // Dont crunch the following (fixed) GroupAddressGroups
      //
      let nextAddress = 0;

      if (a1.address == "0" && a2.address == "0") {
        //
        // 0/0/0 ist not allowed in ETS
        // so start with 0/0/1
        //

        nextAddress = 1;
      }

      if (!a2.isManualGroupAddressFolder && !a2.isEnvironmentFolder) {
        level3Addresses.forEach((a3) => {
          if (!a3.isFixed) {
            const duplicate = level3Addresses.find((a) => a != a3 && a.address == a3.address);
            if (duplicate) {
              a3.address = `${a2.findFirstFreeAddress()}`;

              a3.updateFullAddress();
              a3.raiseOnChange("address");
            }
          }
        });
      }
    });
  });
}

async function updateAllDevices1(devices: Array<Device>) {
  const l = devices.filter((d) => !channelTypeInfo(d.channelType).isSupply).length;
  let i = 1;
  for (const device of devices) {
    if (device.template) {
      const text = `Update ${i++}/${l}`;
      await showToast({
        text: text,
        keepVisible: true,
        modal: true,
        progress: true,
      });
      updateToastProgress({ progress: (100 * i) / l });

      //
      await device.updateAllFromKnxMetadataImmediately();
    }
  }

  updateToastProgress({ progress: 100 });
}

async function updateAllDevices(ask?: boolean): Promise<boolean> {
  let devices = state.allDevices();

  if (restrictToDivicesGlobal) {
    devices = restrictToDivicesGlobal;
  }

  const errorDevices = new Array<Device>();

  for (const device of devices) {
    if (!channelTypeInfo(device.channelType).isSupply && !device.template) {
      errorDevices.push(device);
    }
  }

  if (errorDevices.length >= state.allDevices().length) {
    await state.dialogManager.openDialog(
      <BaseDialog
        noAbortButton={true}
        onOk={() => {
          return true;
        }}
        title={itx("addressGenerator.noSignalsInProject", "Keine Signale im Projekt")}
      >
        <div>
          {itx("adressGenerator.allDevicesWithNoProduct", "Alle Geräten ist noch kein KNX Produkt zugewiesen.")} <br />
          {itx("adressGenerator.noAdressesGenerated", "Somit können keine Gruppenadressen generiert werden.")}
        </div>
      </BaseDialog>,
      true,
    );
    return false;
  }

  if (errorDevices.length > 0 && ask != false) {
    const errorDeviceElms: any = [];
    errorDevices.forEach((device) => {
      errorDeviceElms.push(
        <div>
          {device.fullTag}&nbsp;{device.name}
          <br />
        </div>,
      );
    });

    const res1 = state.dialogManager.openDialog(
      <BaseDialog
        okCaption={itx("yes", "Ja")}
        abortCaption={itx("no", "Nein")}
        title={itx("addressGenerator.warning", "Achtung")}
      >
        <div>
          {errorDevices.length}{" "}
          {itx("adressGenerator.errorDeviceHeader", "Geräten ist noch kein KNX Produkt zugewiesen")} <br />
          {errorDeviceElms}
          {itx(
            "adressGenerator.adressesStillGenerated",
            "Soll die Genererierung der Gruppenadressen trotzdem durchgeführt werden?",
          )}
        </div>
      </BaseDialog>,
      true,
    );

    if (!res1) {
      return false;
    }
  }

  await updateAllDevices1(devices);

  return false;
}

async function askToDeleteUnreferenced(numberOfUnreferenced: number): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    state.dialogManager.openDialog(
      <BaseDialog
        hideXButton={true}
        okCaption={itx("yes", "Ja")}
        abortCaption={itx("no", "Nein")}
        onOk={() => {
          // await showToast({ text: itx("addressGenerator.lscheunbenutztegruppenadressen", "Lösche unbenutzte Gruppenadressen"), keepVisible: true });
          deleteUnreferencedGroupAddresses();
          resolve(true);
          return true;
        }}
        onAbort={() => {
          resolve(true);
          return true;
        }}
      >
        <div>
          {numberOfUnreferenced}
          &nbsp;
          {itx("addressGenerator.unconnectedGA", "Gruppen Addressen sind mit keinem Gerät verbunden.")}
          <br />
          {itx("addressGenerator.deleteGroupadress", "Sollen diese Gruppenadressen entfernt werden?")}
        </div>
      </BaseDialog>,
      true,
    );
  });
}

async function showResult() {
  const allSignals = state.allSignals();
  // nur für sehr alte Geräte relevant
  // allSignals.forEach((signal) => {
  //   let err = signal.groupAddresses.items.length > 4;
  //   signal.flagErrors({ toMuchGroupAdresses: "" }, err);
  // });

  const signalsWithToMuchGroupAddresses = allSignals.filter((signal) => signal.groupAddresses.items.length > 4);

  let modal = signalsWithToMuchGroupAddresses.length == 0;
  // errorif consumer without actorchannels
  const consumerWithoutActorChannels = state.allConsumers().filter((consumer) => !consumer.getConnectedChannel());
  if (modal && consumerWithoutActorChannels.length > 0) {
    modal = false;
  }

  const TempCurrentGaWithoutButton = new Array<Signal>();
  const tempCurrentSignalWithToMuchButton = new Array<Signal>();

  state.allSignals().forEach((signal) => {
    if (signal.errors?.tempCurrentnoButtonUsage && signal.errors?.tempCurrentnoButtonUsage != "") {
      TempCurrentGaWithoutButton.push(signal);
    }
  });
  state.allSignals().forEach((signal) => {
    if (signal.errors?.tempCurrentMultipleButtonUsages && signal.errors?.tempCurrentMultipleButtonUsages != "") {
      tempCurrentSignalWithToMuchButton.push(signal);
    }
  });
  await state.dialogManager.openDialog(
    <GroupAdressResultDialog
      groupAddressGenerationInfos={groupAddressGenerationInfos}
      signalsWithToMuchGroupAddresses={signalsWithToMuchGroupAddresses}
      consumerWithoutActorChannels={consumerWithoutActorChannels}
      tempCurrentSignalWithoutButton={TempCurrentGaWithoutButton}
      tempCurrentSignalWithToMuchButton={tempCurrentSignalWithToMuchButton}
    />,
    modal,
  );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////
//
// MAIN Function
//
/////////////////////////////////////////////////////////////////////////////////////////////////////

export async function generateGroupAddresses(
  ask?: boolean,
  restrictToDevices?: Array<Device>,
  suppressDialog?: boolean,
) {
  //////////////////////////////////////////////////////////////////////////////////////////////////
  // for statistic
  ////////////////
  if (!state.projectMetadata.statistic) {
    state.projectMetadata.updateNewStatistic();
  }
  if (state.projectMetadata.statistic) {
    state.projectMetadata.statistic.numberOfGroupAdressesGeneratings = state.projectMetadata.statistic
      .numberOfGroupAdressesGeneratings++;
  }
  /////////////////////////////////////////////////////////////////////////////////////////////////////

  // do nothing if no devices with product is in project
  if (
    state.allDevices().filter((dev) => {
      return dev.template;
    }).length == 0
  ) {
    state.deleteAllGroupAddresses();
    if (state.projectMetadata.statistic) {
      state.projectMetadata.statistic.numberOfGroupAdresses = 0;
    }
    showToast({
      text: itx(
        "addressGenerator.noDevicesWithProduct",
        "Es sind keine Geräte mit einem KNX Produkt im Projekt vorhanden.",
      ),
      isError: true,
    });
    return;
  }

  restrictToDivicesGlobal = restrictToDevices;

  try {
    if (state.addresses.size != 0) {
      if (isUiModeExpert()) {
        state.showModifiedStatus = true;
        state.command("updateMenubar"); // no itx
      }
    }

    state.consistencyCheck();

    initSignalBundleDefinitions();
    state.settings.applyGroupAddressNameTemplates();

    groupAddressGenerationInfos.deletedGroupAddressCount = 0;
    groupAddressGenerationInfos.createdGroupAddressCount = 0;

    groupAddressGenerationInfos.deletedGroupAddressReferenceCount = 0;
    groupAddressGenerationInfos.newGroupAddressReferenceCount = 0;

    if (state.addresses.size == 0) {
      //
      // call for empty state.addresses will clean up lastGroupAddresses && lastItems
      //

      state.deleteAllGroupAddresses();
    }

    setIsFixedAndIsNewForAllGroupAddresses();

    removeNoLongerUsedSignalsUndGroupAddressesFromConsumers();

    clearAndBackupUsagesForDevices();

    clearAndBackupUsagesForMultiplexer();

    backupChildenOfGroupAddresses();

    GroupAddress.deleteAllGroupAddressesExceptOfManuals();

    clearSignalBackpointersForAllGroupAddresses();

    //
    // create global addresses first
    //
    // 31/7/* is uesed for buttons in Mode "A"
    //
    createGlobalAddresses(state.settings.knxGroupAddressGeneneration);

    switch (state.settings.knxGroupAddressGeneneration) {
      case "A":
        createAddresses_A();
        break;

      case "C":
        createAddresses_C();
        break;

      case "B":
      default:
        createAddresses_B();
        break;
    }

    let doUpdate = false;
    let abort = ask !== false; // when ask=false (programmatic call), proceed without dialog
    if (ask != false && state.guideMode.isHidden) {
      if (suppressDialog != true) {
        await state.dialogManager.openDialog(
          <ConfirmDialog
            text={itx(
              "addressGenerator.updateAllDevicesQuestion",
              "Alle Geräte vor der Erzeugung der Gruppenadressen aktualisieren?",
            )}
            title={itx("addressGenerator.update", "Aktualisieren")}
            onOk={() => {
              doUpdate = true;
              abort = false;
              return true;
            }}
            onAbort={() => {
              doUpdate = false;
              abort = false;
              return true;
            }}
            abortCaption={itx("addressGenerator.ignore", "Überspringen")}
            okCaption={itx("addressGenerator.update", "Aktualisieren")}
            onClose={() => {
              return true;
            }}
          />,
          true,
        );
      } else {
        doUpdate = true;
        abort = false;
      }
    }

    if (abort) {
      return;
    }

    if (suppressDialog) {
      let devices = state.allDevices();
      if (restrictToDivicesGlobal) {
        devices = restrictToDivicesGlobal;
      }
      await updateAllDevices1(devices);
    } else if (doUpdate) {
      await updateAllDevices();
    }

    await showToast({
      text: itx("addressGenerator.bindAdressToActor", "Gruppenadressen an Aktoren binden"),
      keepVisible: true,
    });

    linkActors();

    await showToast({
      text: itx("addressGenerator.bindAdressToButton", "Gruppenadressen an Taster binden"),
      keepVisible: true,
    });

    linkButtons(restrictToDivicesGlobal);

    linkSceneSignals();

    linkEnvironmentsSignals();

    // calcReferencedBy(2);

    showToast({
      text: state.translate("addressGenerator.successGroups", "Gruppenadressen erfolgreich generiert!"),
    });

    const numberOfUnreferenced = countUnreferenced();
    if (numberOfUnreferenced) {
      //
      // für normale Benutzer werden unreferenzierte GruppenAddressen ohne Nachfrage IMMER gelöscht
      //
      if (isUiModeDeveloper() && suppressDialog != true) {
        await askToDeleteUnreferenced(numberOfUnreferenced);
      } else {
        deleteUnreferencedGroupAddresses();
      }
    }

    crunchLevel3GroupAddresses();

    fixDuplicateLevel3GroupAddresses();

    GroupAddress.updateAllBackpointers();

    updateChangesForDevices();

    updateChangesForGroupAddresses();

    findErrors();

    state.addresses.raiseOnChangeUI("@updated");

    state.guideMode.raiseSync({ id: "knxAdressesGenerated" });

    hideToast();
    state.eventLogger.addEvent(EventKey.GroupAddressesGenerated, {
      generatedCount: groupAddressGenerationInfos.createdGroupAddressCount,
      generationMode:
        ask !== false
          ? "Manual" // no itx
          : "Automatic", // no itx
    });
    if (ask != false && suppressDialog != true) {
      await showResult();
    }

    state.consistencyCheck();
    // recheck error inChild elements after generating group addresses
    state.updateAllErrors();
  } catch (ex) {
    logger.error(ex);
    showToast({
      text: state.translate(
        "addressGenerator.errorGenereateAdresses",
        "Beim Generieren der Gruppenadressen ist ein Fehler aufgetreten.",
      ),
      isError: true,
    });
  }
}

//
// returns true if the user clicked Update or Skip, false if the dialog was cancelled/closed
//
export async function askForGroupAddressUpdate(askForSkipping: boolean): Promise<boolean> {
  let shouldContinue = false;
  let shouldUpdateGroupAddresses = false;

  await state.dialogManager.openDialog(
    <BaseDialog
      title={itx("addressGenerator.update", "Aktualisieren")}
      noAbortButton={askForSkipping}
      moreButtons={
        askForSkipping ? (
          <button
            id="BaseDialog_abort"
            className="button"
            onClick={() => {
              shouldContinue = true;
              state.dialogManager.closeDialog(true);
            }}
          >
            {itx("addressGenerator.ignore", "Überspringen")}
          </button>
        ) : undefined
      }
      okCaption={itx("addressGenerator.update", "Aktualisieren")}
      onOk={() => {
        shouldContinue = true;
        shouldUpdateGroupAddresses = true;
        return true;
      }}
    >
      {/* schow shema group adresses */}
      <div
        style={{
          maxWidth: "700px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          paddingLeft: "16px",
        }}
      >
        {/* Aktuelle Konfiguration */}{" "}
        <div
          style={{
            fontWeight: 500,
            fontSize: "1rem",
          }}
        >
          {itx("addressGenerator.usedShema", "Aktuelle Einstellungen für die Gruppenadressierung:")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            color: "var(--textColorSecondary)",
          }}
        >
          <span style={{ marginRight: "6px" }}>•</span>

          {itx(
            "SettingsScreen.gAOption" + state.settings.knxGroupAddressGeneneration,
            "B: (Etage & Raum) / Typ / (Funktion & Signal)",
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            color: "var(--textColorSecondary)",
          }}
        >
          <span style={{ marginRight: "6px" }}>•</span>

          {state.settings.deleteNotUsedSignalTypes
            ? itx(
                "addressGenerator.deleteUnusedSignalTypes",
                "Nicht verwendete Signaltypen werden automatisch entfernt.",
              )
            : itx("addressGenerator.keepUnusedSignalTypes", "Nicht verwendete Signaltypen bleiben erhalten.")}
        </div>
        {/* Hinweis */}
        <button
          className="button"
          style={{ alignSelf: "start", width: "fit-content" }}
          onClick={() => state.dialogManager.openDialog(<SettingsDialog defaultPane="groupAddresses" />, true)}
        >
          <i className="las la-cog" style={{ marginRight: "6px" }} />
          {itx("addressGenerator.openConfiguration", "Einstellung öffnen")}
        </button>
        <br />
        {/* Frage */}
        <div
          style={{
            fontWeight: 500,
            fontSize: "1rem",
          }}
        >
          {itx(
            "addressGenerator.updateAllGroupAdresses",
            "Sollen die Gruppenadressen jetzt für das gesamte Projekt aktualisiert werden?",
          )}
        </div>
      </div>
    </BaseDialog>,
    true,
  );

  if (shouldUpdateGroupAddresses) {
    await generateGroupAddresses(undefined, undefined, false);
  }
  return shouldContinue;
}
